import React, { useState, useEffect, useRef } from "react";
import { FlaskConical, Building2, Sparkles, Monitor, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";

const tabs = [
  {
    label: "AI Labs",
    icon: FlaskConical,
    title: "Interactive AI Labs",
    description: "Build, experiment, and apply AI using real-world datasets, Data Recipes and Model Recipes — from no-code workflows to advanced model development.",
    bullets: [
      { text: "Work on real datasets across use cases", bold: [] },
      { text: "Drag-and-drop and code-based environments", bold: ["Drag-and-drop"] },
      { text: "Apply models and run experiments", bold: [] },
      { text: "100+ Lab Templates across Industries and Use Cases", bold: ["100+ Lab"] },
    ],
  },
  {
    label: "Industry Labs",
    icon: Building2,
    title: "Industry Labs",
    description: "Understand how AI is applied across industries through real simulations, datasets, and workflows.",
    bullets: [
      { text: "Use cases across Healthcare, Automotive, Retail, Industrial, & more", bold: ["Healthcare, Automotive, Retail, Industrial,"] },
      { text: "Real-world datasets and dashboards", bold: ["Real-world datasets"] },
      { text: "Industry-specific simulations", bold: [] },
      { text: "Learn by applying AI in real contexts", bold: ["Learn by applying AI"] },
    ],
  },
  {
    label: "AI Course Library",
    icon: Sparkles,
    title: "AI Courses Across Fields",
    description: "AI programs designed for universities and enterprises across disciplines.",
    bullets: [
      { text: "50+ AI courses across domains", bold: ["50+ AI courses"] },
      { text: "Curriculum aligned with real-world applications", bold: ["real-world applications"] },
      { text: "Designed for multiple roles and functions", bold: ["Designed for multiple roles"] },
      { text: "Programs for foundational and advanced learning", bold: [] },
    ],
  },
  {
    label: "AI-Native LMS",
    icon: Monitor,
    title: "AI-Native LMS",
    description: "A learning system built to deliver, manage, and scale AI education.",
    bullets: [
      { text: "Personalized learning journeys", bold: [] },
      { text: "Course and content creation AI Tools", bold: [] },
      { text: "Analytics and performance tracking", bold: [] },
      { text: "Automated Assessments and Evaluations", bold: [] },
    ],
  },
];

/* Tab 1: AI Labs screenshot */
const AILabsScreenshot = () => (
  <div className="h-full w-full">
    <img 
      src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/beughe17_Copy%20of%20Interactive%20AI%20Labs.png"
      alt="Interactive AI Labs - Automation workflow"
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </div>
);

/* Tab 2: Industry Labs screenshot */
const IndustryLabsScreenshot = () => (
  <div className="h-full w-full">
    <img 
      src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/y19lre0z_Copy%20of%20industry%20labs.png"
      alt="Industry Labs - Demand Forecast Analysis"
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </div>
);

/* Tab 3: AI Course Library screenshot */
const CourseLibraryScreenshot = () => (
  <div className="h-full w-full">
    <img 
      src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/w4bq9hhl_Copy%20of%20ai%20courses.png"
      alt="AI Courses - Courses management"
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </div>
);

/* Tab 4: AI-Native LMS screenshot */
const LMSScreenshot = () => (
  <div className="h-full w-full">
    <img 
      src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/te12ofth_Copy%20of%20ai%20lms.png"
      alt="AI-Native LMS - Learner Metrics"
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </div>
);

const screenshots = [AILabsScreenshot, IndustryLabsScreenshot, CourseLibraryScreenshot, LMSScreenshot];

const BulletText = ({ text, bold }) => {
  if (!bold || bold.length === 0) return <span>{text}</span>;
  let result = text;
  const parts = [];
  let remaining = text;
  for (const b of bold) {
    const idx = remaining.indexOf(b);
    if (idx >= 0) {
      if (idx > 0) parts.push({ text: remaining.slice(0, idx), isBold: false });
      parts.push({ text: b, isBold: true });
      remaining = remaining.slice(idx + b.length);
    }
  }
  if (remaining) parts.push({ text: remaining, isBold: false });
  if (parts.length === 0) return <span>{text}</span>;
  return (
    <span>
      {parts.map((p, i) => p.isBold ? <strong key={i} className="font-bold text-[#1a1a2e]">{p.text}</strong> : <span key={i}>{p.text}</span>)}
    </span>
  );
};

const PlatformSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [cardTransforms, setCardTransforms] = useState([]);
  const sectionRef = useRef(null);
  const anchorsRef = useRef([]);
  const tickingRef = useRef(false);

  // Track scroll position and apply smooth transforms to cards (throttled with rAF)
  useEffect(() => {
    const computeTransforms = () => {
      tickingRef.current = false;
      if (!anchorsRef.current.length) return;

      const windowHeight = window.innerHeight;
      const centerY = windowHeight / 2;
      const transforms = [];
      let bestIndex = 0;
      let bestDistance = Infinity;

      // Compute distance between consecutive anchors (gap = prev card's flow height + marginTop)
      // Use the difference between any two anchors' offsetTop as the canonical step.
      let STEP = windowHeight * 0.8; // sane fallback
      const a0 = anchorsRef.current[0];
      const a1 = anchorsRef.current[1];
      if (a0 && a1) {
        const measured = a1.offsetTop - a0.offsetTop;
        if (measured > 100) STEP = measured;
      }

      anchorsRef.current.forEach((anchor, index) => {
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        // scrollProgress: 0 when anchor reaches sticky line (160px), 1 when next anchor reaches sticky line
        const scrollProgress = (160 - rect.top) / STEP;

        // Distance to viewport center for active-tab detection
        const distanceFromCenter = Math.abs(rect.top + windowHeight * 0.35 - centerY);

        let scale = 1;
        let opacity = 1;
        let translateY = 0;

        if (scrollProgress > 0.85) {
          // Next card has nearly arrived — fade this one out
          const stackProgress = Math.min((scrollProgress - 0.85) / 0.15, 1);
          scale = 1 - stackProgress * 0.05;
          opacity = 1 - stackProgress * 0.95;
          translateY = -stackProgress * 24;
        } else if (scrollProgress < -0.05) {
          // Card hasn't reached its sticky position yet (still in flow below)
          const enterProgress = Math.min(Math.abs(scrollProgress + 0.05) * 1.5, 1);
          scale = 1 - enterProgress * 0.04;
          opacity = 1 - enterProgress * 0.6;
        }

        transforms[index] = { scale, opacity, translateY };

        if (distanceFromCenter < bestDistance) {
          bestDistance = distanceFromCenter;
          bestIndex = index;
        }
      });

      setCardTransforms(transforms);
      setActiveTab((prev) => (prev === bestIndex ? prev : bestIndex));
    };

    const handleScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(computeTransforms);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    computeTransforms();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Smooth scroll to a specific card using its non-sticky anchor position
  const scrollToCard = (index) => {
    const anchor = anchorsRef.current[index];
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    // Sticky tabs nav sits at top-20 + ~64px tall → leave ~160px safe offset
    const offset = 160;
    const targetY = window.scrollY + rect.top - offset + 2;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} id="platform-section" className="pt-20 md:pt-28 pb-6 md:pb-8 bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Heading */}
        <h2
          className="text-center text-[#1a1a2e] max-w-[650px] mx-auto mb-4 leading-tight"
          style={{ fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 800 }}
        >
          One platform to learn, practice, and apply AI
        </h2>
        <p className="text-center text-gray-500 text-[16px] max-w-[550px] mx-auto mb-10 leading-relaxed">
          A quick walkthrough of how Elevante enables hands-on AI learning across real-world use cases.
        </p>

        {/* Sticky Tabs — desktop only */}
        <div className="hidden md:block sticky top-20 z-20 bg-white pb-6 mb-10">
          <div className="flex items-center justify-start md:justify-center gap-4 sm:gap-6 md:gap-8 overflow-x-auto scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0">
            {tabs.map((t, i) => {
              const Icon = t.icon;
              const isActive = activeTab === i;
              return (
                <button
                  key={i}
                  onClick={() => scrollToCard(i)}
                  className={`flex items-center gap-2 pb-2 border-b-2 transition-all duration-300 text-[14px] md:text-[15px] whitespace-nowrap flex-shrink-0 ${
                    isActive
                      ? "border-[#8B5CF6] text-[#1a1a2e] font-bold"
                      : "border-transparent text-gray-500 font-medium hover:text-gray-700"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-[#8B5CF6]" : "text-gray-400"}`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* MOBILE: Simple vertical stack — no sticky, no animation, no tabs */}
        <div className="md:hidden flex flex-col gap-6">
          {tabs.map((tab, index) => {
            const TabIcon = tab.icon;
            const Screenshot = screenshots[index];
            return (
              <div
                key={index}
                className="rounded-2xl border border-[#E5E0F2] overflow-hidden bg-[#FAF7FF]"
              >
                {/* Image on top */}
                <div className="relative overflow-hidden bg-white">
                  <Screenshot />
                </div>
                {/* Content below */}
                <div className="p-6 bg-white">
                  <TabIcon className="w-8 h-8 text-[#8B5CF6] mb-3" />
                  <h3
                    className="text-[22px] text-[#1a1a2e] mb-3 leading-snug"
                    style={{ fontWeight: 800 }}
                  >
                    {tab.title}
                  </h3>
                  <p className="text-[14px] text-gray-500 leading-relaxed mb-5">
                    {tab.description}
                  </p>
                  <div className="space-y-3">
                    {tab.bullets.map((bullet, bi) => (
                      <div key={bi} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-[13.5px] text-gray-600 leading-relaxed">
                          <BulletText text={bullet.text} bold={bullet.bold} />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* DESKTOP: Stacked Cards — single shared parent so all sticky cards overlap at the same offset.
            Anchors (0-height) sit at each card's natural document position for accurate scroll targets. */}
        <div className="hidden md:block relative">
          {tabs.map((tab, index) => {
            const TabIcon = tab.icon;
            const Screenshot = screenshots[index];
            const transform = cardTransforms[index] || { scale: 1, opacity: 1, translateY: 0 };

            return (
              <React.Fragment key={index}>
                {/* Anchor: invisible 0-height marker at the natural document position of card N */}
                <div
                  ref={(el) => (anchorsRef.current[index] = el)}
                  aria-hidden="true"
                  style={{ height: 0, marginTop: index === 0 ? 0 : '20vh' }}
                />
                <div
                  className="sticky top-[160px] will-change-transform"
                  style={{
                    transform: `translateY(${transform.translateY}px) scale(${transform.scale})`,
                    opacity: transform.opacity,
                    zIndex: index + 1,
                    pointerEvents: transform.opacity < 0.1 ? 'none' : 'auto',
                  }}
                >
                  <div className="max-w-[960px] mx-auto rounded-2xl border border-[#A7A2B4] overflow-hidden bg-white shadow-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 min-h-[480px]">
                      {/* Left — Screenshot (full space, no purple header or icon) */}
                      <div className="relative overflow-hidden">
                        <Screenshot />
                      </div>

                      {/* Right — Description */}
                      <div className="p-8 md:p-10 flex flex-col justify-center">
                        <TabIcon className="w-10 h-10 text-[#8B5CF6] mb-4" />
                        <h3
                          className="text-[24px] md:text-[28px] text-[#1a1a2e] mb-3 leading-snug"
                          style={{ fontWeight: 800 }}
                        >
                          {tab.title}
                        </h3>
                        <p className="text-[15px] text-gray-500 leading-relaxed mb-6">
                          {tab.description}
                        </p>
                        <div className="space-y-3">
                          {tab.bullets.map((bullet, bi) => (
                            <div key={bi} className="flex items-start gap-3">
                              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span className="text-[14px] text-gray-600 leading-relaxed">
                                <BulletText text={bullet.text} bold={bullet.bold} />
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Spacer to allow last card to remain readable (desktop only) */}
        <div className="hidden md:block h-[8vh]" />
      </div>
    </section>
  );
};

export default PlatformSection;
